"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Search, Compass, Code, TrendingUp, Clock, CircleCheck as CheckCircle2, ArrowRight, UserCheck, Sparkles } from 'lucide-react';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const stepsData = [{
  id: 1,
  number: "01",
  title: "Discovery & Technical Audit",
  timeline: "Scope Dependent",
  description: "I perform a comprehensive technical, content, and competitor audit of your website to uncover hidden ranking limiters and isolate high-value search opportunities.",
  iconName: "Search"
}, {
  id: 2,
  number: "02",
  title: "Strategy Development",
  timeline: "Scope Dependent",
  description: "I construct a bespoke national search engine optimisation roadmap tailored to your business goals, target UK audiences, and competitive search engine growth timelines.",
  iconName: "Compass"
}, {
  id: 3,
  number: "03",
  title: "Hands-on Implementation",
  timeline: "Scope Dependent",
  description: "Through targeted national SEO services, I directly implement or oversee technical fixes, structured schema data, page speed enhancements, and high-intent content optimization.",
  iconName: "Code"
}, {
  id: 4,
  number: "04",
  title: "Monitoring & Optimisation",
  timeline: "Ongoing Monthly",
  description: "I continuously track rankings, adapt to Search and AI engine changes, and refine target keywords to compound sustainable long-term organic traffic.",
  iconName: "TrendingUp"
}];
const deliverablesData = [{
  id: "d1",
  stepId: 1,
  text: "Technical & crawlability health audit"
}, {
  id: "d2",
  stepId: 1,
  text: "Keyword intent & competitor gap analysis"
}, {
  id: "d3",
  stepId: 1,
  text: "Indexation & site architecture review"
}, {
  id: "d4",
  stepId: 2,
  text: "Custom prioritized action roadmap"
}, {
  id: "d5",
  stepId: 2,
  text: "Content architecture & hierarchy strategy"
}, {
  id: "d6",
  stepId: 2,
  text: "SEO KPI & baseline measurement framework"
}, {
  id: "d7",
  stepId: 3,
  text: "On-page & technical SEO code fixes"
}, {
  id: "d8",
  stepId: 3,
  text: "Schema markup & structured data integration"
}, {
  id: "d9",
  stepId: 3,
  text: "High-intent content optimization"
}, {
  id: "d10",
  stepId: 4,
  text: "Transparent monthly performance reporting"
}, {
  id: "d11",
  stepId: 4,
  text: "AI search visibility & overview tracking"
}, {
  id: "d12",
  stepId: 4,
  text: "Continuous keyword expansion & refinement"
}];
export default function Process() {
  const [activeStep, setActiveStep] = useState(1);
  const shouldReduceMotion = useReducedMotion();
  const getStepIcon = (iconName: string) => {
    switch (iconName) {
      case "Search":
        return <Search className="size-5 text-primary" />;
      case "Compass":
        return <Compass className="size-5 text-primary" />;
      case "Code":
        return <Code className="size-5 text-primary" />;
      case "TrendingUp":
        return <TrendingUp className="size-5 text-primary" />;
      default:
        return <CheckCircle2 className="size-5 text-primary" />;
    }
  };
  const currentStepData = stepsData.find(s => s.id === activeStep) || stepsData[0];
  const currentDeliverables = deliverablesData.filter(d => d.stepId === activeStep);
  return <section id="methodicalprocesstimeline" data-nav="light" className="relative bg-background py-20 md:py-28 font-sans text-foreground overflow-hidden">
      <div className="pointer-events-none absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 size-[600px] rounded-full bg-secondary/30 blur-3xl opacity-50" />

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-16 md:mb-20">
          <Badge variant="secondary" className="mb-4 px-3 py-1 bg-secondary text-secondary-foreground font-medium rounded-full">
            <Sparkles className="size-3.5 text-accent mr-1.5" />
            Methodical Growth Engine
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif tracking-tight text-foreground leading-tight mb-6">
            A Step-by-Step SEO Methodology Built for Results
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            {"A structured, four-phase national SEO process designed to uncover site bottlenecks, formulate a strategic roadmap, execute technical fixes, and compound monthly search performance. Exact timelines depend on site size, current technical health and competitive landscape."}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-12">
          {stepsData.map((step, i) => {
          const isActive = step.id === activeStep;
          return <div data-index={i} key={step.id} className="relative">
                <Card className={`h-full transition-all duration-300 border ${isActive ? "border-primary bg-card shadow-lg ring-1 ring-primary/20" : "border-border bg-card/60 hover:bg-card hover:border-border/80 shadow-xs"}`}>
                  <CardContent className="p-6 flex flex-col justify-between h-full">
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-2xl font-bold font-serif text-primary">
                          {step.number}
                        </span>
                        <Badge variant="outline" className="text-xs font-normal border-border bg-muted/50 text-muted-foreground flex items-center gap-1">
                          <Clock className="size-3 text-muted-foreground" />
                          {step.timeline}
                        </Badge>
                      </div>

                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2.5 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          {getStepIcon(step.iconName)}
                        </div>
                        <h3 className="font-semibold text-lg text-foreground font-serif leading-snug">
                          {step.title}
                        </h3>
                      </div>

                      <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                        {step.description}
                      </p>
                    </div>

                    <Button variant={isActive ? "default" : "outline"} size="sm" onClick={() => setActiveStep(step.id)} className="w-full justify-between mt-auto text-xs font-medium">
                      <span>
                        {isActive ? "Currently Viewing" : "Explore Phase"}
                      </span>
                      <ArrowRight className="size-3.5" />
                    </Button>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>

        <motion.div initial={shouldReduceMotion ? {} : {
        opacity: 0,
        y: 15
      }} whileInView={shouldReduceMotion ? {} : {
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4
      }} className="max-w-5xl mx-auto">
          <div className="bg-card border border-border rounded-2xl p-6 sm:p-8 md:p-10 shadow-md relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-primary" />

            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-7 space-y-6">
                <div className="flex items-center gap-3">
                  <Badge variant="default" className="bg-primary text-primary-foreground">
                    Phase {currentStepData.number}
                  </Badge>
                  <span className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                    <Clock className="size-4 text-accent" />
                    Estimated Timeline: {currentStepData.timeline}
                  </span>
                </div>

                <h3 className="text-2xl sm:text-3xl font-bold font-serif text-foreground">
                  {currentStepData.title}
                </h3>

                <p className="text-muted-foreground text-base leading-relaxed">
                  {currentStepData.description}
                </p>

                <div className="space-y-3 pt-2">
                  <p className="text-xs font-semibold tracking-wider text-foreground uppercase">
                    Key Deliverables You Receive:
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {currentDeliverables.map((item, i) => <div data-index={i} key={item.id} className="flex items-start gap-2 text-sm text-foreground/90 bg-muted/40 p-2.5 rounded-md border border-border/50">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{item.text}</span>
                      </div>)}
                  </div>
                </div>
              </div>

              <div className="md:col-span-5 bg-muted/30 border border-border rounded-xl p-6 flex flex-col justify-between space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="size-12 rounded-full overflow-hidden border-2 border-primary shrink-0 bg-background">
                      <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png-wvc-sizes" alt="Yassine Essaadi - National SEO Consultant" className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div>
                      <p className="font-bold text-foreground font-serif text-sm">
                        Yassine Essaadi
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Independent SEO Consultant
                      </p>
                    </div>
                  </div>

                  <div className="p-3 bg-background rounded-lg border border-border/80 text-xs text-muted-foreground leading-relaxed italic">
                    {"\"Every stage builds methodically on the previous one: thorough diagnostic audits establish the baseline, strategic roadmaps define priorities, hands-on fixes execute changes, and continuous monitoring compounds rankings.\""}
                  </div>

                  <div className="space-y-2 text-xs">
                    <div className="flex items-center gap-2 text-foreground font-medium">
                      <UserCheck className="size-4 text-primary" />
                      <span>Structured 4-Phase Workflow</span>
                    </div>
                  </div>
                </div>

                <div className="pt-2">
                  <Link to="/ContactPage" className="w-full block">
                    <Button variant="default" size="default" className="w-full justify-center gap-2 font-medium">
                      <span>Book a Free SEO Consultation</span>
                      <ArrowRight className="size-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="mt-16 text-center">
          <p className="text-sm text-muted-foreground mb-4">
            {"Ready to see how this process applies to your site's specific growth potential?"}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/ContactPage">
              <Button size="lg" className="px-6 font-medium">
                Start With a Discovery Call
              </Button>
            </Link>
            <Link to="/CaseStudiesPage">
              <Button size="lg" variant="outline" className="px-6 font-medium">
                Explore Case Studies
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>;
}